/**
 * TableArtifact — renders a structured table that came back from a tool
 * call. Expects { columns: [{key, label, format?}], rows: [{...}] } shape
 * — that's the contract the backend should follow per design-lock §9.
 *
 * Falls back to a JSON pre-block if the shape doesn't match, so an
 * unexpected payload still shows something instead of crashing.
 */

function isTableShape(data) {
  return (
    data &&
    Array.isArray(data.columns) &&
    Array.isArray(data.rows) &&
    data.columns.every((c) => c && typeof c.key === 'string')
  )
}

function formatCell(value, formatter) {
  if (value == null) return '—'
  if (typeof formatter === 'function') {
    try {
      return formatter(value)
    } catch (e) {
      return String(value)
    }
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch (e) {
      return String(value)
    }
  }
  return String(value)
}

/**
 * @param {object} props
 * @param {object} props.data — { columns, rows }
 * @param {string} [props.caption]
 */
export function TableArtifact({ data, caption }) {
  if (!isTableShape(data)) {
    // Don't leak raw JSON to the customer (it shouldn't get here at all now
    // that non-canvas artifacts aren't pinned — see chatStore
    // NON_CANVAS_ARTIFACT_TYPES). Show a calm message; keep the raw payload
    // for developers only.
    return (
      <div class="ion-table-artifact ion-table-artifact--fallback">
        <p class="ion-table-artifact__hint">
          This result can’t be shown as a table.
        </p>
        {import.meta.env.DEV && (
          <pre class="ion-table-artifact__raw">
            {JSON.stringify(data, null, 2)}
          </pre>
        )}
      </div>
    )
  }

  const { columns, rows } = data

  return (
    <div class="ion-table-artifact">
      {caption && <p class="ion-table-artifact__caption">{caption}</p>}
      <div class="ion-table-artifact__scroll">
        <table class="ion-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key} scope="col">
                  {col.label || col.key}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={row.id || idx}>
                {columns.map((col) => (
                  <td key={col.key}>{formatCell(row[col.key], col.format)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
